import {
  Divider,
  Grid,
  Stack,
  Text
} from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto, InsuranceParamsTravelDto, Offer, OfferPackage, OfferProperty, Part, PartPackage } from 'BrokerTsLib/src/dtos'
import { Insurer, PackageItemLimitType, Product, Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { DateSelector } from '@/containers/dashboard/Stats/DateSelector'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'

import { format } from 'date-fns'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useSelectedQuote } from '../../context/SelectedQuoteContext'
import { useGetInsurerInfo, useGetRiskVariants } from '../../hooks'
import { CompanyLogo } from '../CompanyLogo'
import { CompletedOverview } from '../CompletedOverview'
import { RecapSummary } from '../RecapSummary'
import { RecapSidebar, SectionItem } from '../RecapSummary/RecapSidebar'
import { RecapTable } from '../RecapTable'
import type { RecapTableRowProps } from '../RecapTable/RecapTableRow'

export const getRecapItem = (title: string, parts: Part[]) => {
  return {
    title,
    items: parts.map(x => {
      return {
        label: x.nameNew ?? x.name,
        value: x.withoutPrice ? '' : priceFormat(x.price, undefined, x.parentId ? '' : undefined),
        labelProps: x.parentId ? { sx: { ml: 5, fontWeight: 400 } } : {}
      }
    })
  }
}

export const getPackageRecapItem = (title: string, parts: PartPackage[]): RecapTableRowProps => {
  return {
    title: title,
    items: parts.map(x => {
      const limitText = x.limit === 999999999
        ? 'Neomezeně'
        : x.limit
          ? priceFormat(x.limit, undefined, x.parentId ? '' : undefined)
          : x.packageItem?.limitType === PackageItemLimitType.Limit
            ? '0 Kč'
            : 'Ano'

      return {
        label: x.nameNew ?? x.name,
        value: limitText,
        labelProps: x.parentId ? { sx: { ml: 5, fontWeight: 400 } } : {}
      }
    })
  }
}

export const getPackageRecapData = (offer: OfferPackage, title: string) => {
  const recapData = []
  recapData.push(getPackageRecapItem(title, offer.parts
    .filter(p => p.included && !(p.packageItem?.isAddition ?? false))
    .sort((a, b) => a.name.localeCompare(b.name))))

  return recapData
}

export const getRecapData = (offer: Offer, app: Apps) => {
  const povPart = offer?.parts.find(x => x.riskId === Risk.POV)
  const havPart = offer?.parts.find(x => x.riskId === Risk.HAV)
  const gapPart = offer?.parts.find(x => x.riskId === Risk.GAP && x.valid && x.included)
  let spolPartName = offer?.parts.find(x => x.riskId === Risk.SPOL)?.name
  if (havPart && havPart.name && spolPartName) {
    if (havPart.name.toLowerCase().indexOf('spoluúčast') >= 0)
      spolPartName = undefined
  }
  const recapData = []
  const addons = []

  if (offer.insurerId == Insurer.Allianz && app == Apps.vehicle) {
    // Pro Allianz zobrazovat obsah balíčku - bez cen jednotlivých partů
    const povText = povPart?.nameNew ?? povPart?.name
    const havText = `${havPart?.nameNew ?? havPart?.name}, ${spolPartName}`
    const allianzPackageText = havPart ? havText : povText
    const glassPart = offer.parts.find(x => x.riskId === Risk.SKLA)

    const lbl: ReactNode = <Stack gap={2}>
      {povPart?.description && <Text dangerouslySetInnerHTML={{ __html: povPart.description ?? '' }}></Text>}
      {havPart?.description && <Text dangerouslySetInnerHTML={{ __html: havPart.description ?? '' }}></Text>}
      {glassPart?.description && <Text dangerouslySetInnerHTML={{ __html: glassPart.description ?? '' }}></Text>}
    </Stack>

    recapData.push({ title: allianzPackageText!, items: [{ label: lbl }] })

    const addonsParts = offer.parts.filter(x => ![Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP, Risk.SKLA].includes(x.riskId) && x.included)
    if (offer?.insurerId == Insurer.Allianz || addonsParts.every(x => x.price == 0))
      addonsParts.forEach(x => x.withoutPrice = true)

    addons.push(...addonsParts)
  }
  else {
    if (povPart)
      recapData.push(getRecapItem('Povinné ručení', [povPart]))

    if (havPart)
      recapData.push(getRecapItem('Havarijní pojištění', [
        { ...havPart, nameNew: `${havPart.nameNew ?? havPart.name}${spolPartName ? `, ${spolPartName}` : ''}` },
        ...(gapPart ? [gapPart] : [])
      ]))

    addons.push(...offer.parts.filter(x => ![Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP].includes(x.riskId) && x.included))
  }

  if (addons.length > 0)
    recapData.push(getRecapItem('Připojištění', addons))

  return recapData
}

const InformationTab = {
  Recap: 0,
  Info: 1,
  AdditionalDates: 2
}
export type InformationTab = typeof InformationTab[keyof typeof InformationTab]

export const RecapQuote = () => {
  const { app } = useApp()
  const { insuranceParams } = useInsuranceParams()
  const { offer } = useSelectedQuote()
  const { data: dataInsurer, refetch: refetchInsurer } = useGetInsurerInfo(offer!.insurerId, { enabled: false, retry: false })
  const insurer = dataInsurer?.data
  const ipt = insuranceParams as InsuranceParamsTravelDto

  useEffect(() => {
    if (offer?.insurerId)
      refetchInsurer()
  }, [offer])

  const sectionItems = [
    <SectionItem label="Počátek pojištění">
      <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
        {format(new Date(insuranceParams!.generalParams.insuranceStartDate!), 'd. MMMM yyyy')}
      </Text>
    </SectionItem>
  ]

  if (app == Apps.travel && !!ipt) {
    if (ipt.travel.insuranceEndDate)
      sectionItems.push((
        <SectionItem label="Konec pojištění">
          <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
            {format(new Date(ipt.travel.insuranceEndDate), 'd. MMMM yyyy')}
          </Text>
        </SectionItem>
      ))
  }

  const isPropertyOrTravel = ([Apps.travel, Apps.property] as Apps[]).includes(app!)
  const isTravel = app == Apps.travel
  const isProperty = app == Apps.property

  return (
    <>
      {/* Recap info */}
      {offer &&
        <RecapSummary
          sectionTitle="Rekapitulace pojištění"
          number={1}
          offer={offer}
          bodyProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 6,
              // gridTemplateRows: 'auto auto 1fr auto',
            },
          }}
          sidebar={
            <>
              {insurer &&
                <RecapSidebar
                  logo={<CompanyLogo insurer={{ id: insurer.id, name: insurer.name }} offerType={offer.type} p={8} maxW='240px' />}
                  website={insurer.url}
                  email={insurer.email}
                  paymentFrequencyId={offer.paymentFrequencyId!}
                  paymentPrice={offer.priceFrequency}
                  totalPrice={offer.price}
                  completed={false}
                  sectionItems={sectionItems}
                />
              }
            </>
          }
        >
          {!isPropertyOrTravel &&
            <RecapTable
              heading="Pojistné/rok"
              rows={getRecapData(offer, app!)}
              total={priceFormat(offer.price)}
            />
          }

          {isTravel &&
            <RecapTable
              heading=''
              rows={getPackageRecapData(offer as OfferPackage, 'Rozsah pojištění')}
              total={priceFormat(offer.price)}
            />
          }

          {isProperty &&
            <PropertySummary offer={offer as OfferProperty} />
          }

        </RecapSummary>
      }
    </>
  )
}

export const PropertySummary = ({ offer }: { offer: OfferProperty }) => {
  const { product } = useApp()
  const { data: dataPropertyTypes } = useGetRiskVariants(Risk.PROPERTY_TYPE)
  const complicityPart = offer?.parts.find(x => x.riskId == Risk.SPOL_PROPERTY)
  const complicityFloodPart = offer?.parts.find(x => x.riskId == Risk.SPOL_FLOOD)
  const floodPart = offer.parts.find(x => x.riskId == Risk.FLOOD && x.included)

  const complicityText = []
  if (complicityPart)
    complicityText.push(priceFormat(complicityPart.limit, null, '0 Kč'))

  if (complicityFloodPart && floodPart)
    complicityText.push('povodeň ' + priceFormat(complicityFloodPart.limit, null, '0 Kč'))

  const [selectedTab, setSetselectedTab] = useState<InformationTab>(InformationTab.Info)
  const tabs = [
    { name: 'Informace', value: InformationTab.Info },
    { name: 'Rekapitulace', value: InformationTab.Recap }
  ]

  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsPropertyDto
  const variantPart = insuranceParams.property.variants?.find(x => x.id == offer?.insuranceVariantId)?.variantParts?.find(x => x.risk == Risk.PROPERTY_TYPE)?.riskVariant

  const title = product == Product.LiabilityInsurance
    ? `Samostatná odpovědnost „${offer?.liabilityPackageName}”`
    : `${dataPropertyTypes?.data.find(x => x.id == variantPart)?.name} „${[...new Set([offer.householdPackageName, offer.realtyPackageName].filter(x => !!x))].join(' + ')}”`

  return (
    <>
      {selectedTab == InformationTab.Recap &&
        <PropertyRecapTable offer={offer} />
      }

      {selectedTab == InformationTab.Info &&
        <CompletedOverview
          items={[
            { label: 'Typ pojištění', value: title },
            { label: 'Spoluúčast', value: complicityText.join(', ') }
          ]}
          sx={{
            gridTemplateColumns: '1fr',
            gap: 0
          }}
        />
      }

      <Divider sx={{ alignSelf: 'end', justifySelf: 'end', mt: 'auto' }} />

      <Grid mb={8}>
        <DateSelector
          sx={{ mx: 'auto' }}
          options={tabs}
          value={selectedTab}
          onChange={(value) => setSetselectedTab(value)}
        />
      </Grid>
    </>
  )
}

export const PropertyRecapTable = ({ offer }: { offer: OfferProperty }) => {
  const complicityPart = offer?.parts.find(x => x.riskId == Risk.SPOL_PROPERTY)
  const complicityFloodPart = offer?.parts.find(x => x.riskId == Risk.FLOOD && x.included) ?
    offer?.parts.find(x => x.riskId == Risk.SPOL_FLOOD) : undefined
  const responsibilityPart = offer?.parts.find(x => x.riskId == Risk.RESPONSIBILITY && x.included)
  const responsibilityRealestatePart = offer?.parts.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE && x.included)

  const parts = offer.parts.filter(p =>
    p.included
    && p.riskId
    && ![responsibilityPart, responsibilityRealestatePart].includes(p)
    && ![Risk.SPOL_PROPERTY, Risk.SPOL_FLOOD].includes(p.riskId)
  )
  const householdItems = parts.filter(x => x.packageItem.productTypeRiskVariantId == RiskVariant.Household)
  const realtyItems = parts.filter(x => x.packageItem.productTypeRiskVariantId == RiskVariant.Realty)
  const otherParts = parts.filter(x => !x.packageItem.productTypeRiskVariantId)
  const otherItems = [responsibilityPart, responsibilityRealestatePart, complicityPart, complicityFloodPart, ...otherParts].filter(x => !!x)

  return (
    <>
      {
        householdItems.length > 0 &&
        <RecapTable
          heading='Domácnost'
          totalProps={{ borderBottom: 'none' }}
          headingProps={{
            sx: {
              justifySelf: 'start',
              fontSize: 'xl',
            }
          }}
          rows={[getPackageRecapItem('', householdItems.sort((a, b) => a.name.localeCompare(b.name)))]}
        />
      }

      {realtyItems.length > 0 &&
        <RecapTable
          heading='Nemovitost'
          totalProps={{ borderBottom: 'none' }}
          headingProps={{
            sx: {
              justifySelf: 'start',
              fontSize: 'xl',
            }
          }}
          rows={[getPackageRecapItem('', realtyItems.sort((a, b) => a.name.localeCompare(b.name)))]}
        />
      }

      {otherItems.length > 0 &&
        <RecapTable
          heading=''
          totalProps={{ borderBottom: 'none' }}
          headingProps={{
            sx: {
              justifySelf: 'start',
              fontSize: 'xl',
            }
          }}
          rows={[getPackageRecapItem('', otherItems.sort((a, b) => a.name.localeCompare(b.name)))]}
        />
      }
    </>
  )
}